import { forwardRef, useId, useRef, useState } from "react";

import dayjs, { type Dayjs } from "@eisox/dayjs";
import { CalendarIcon } from "@eisox/icons";
import { composeEventHandlers } from "@radix-ui/primitive";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { cn } from "..";
import { EMPTY_DATE_RANGE } from "../Calendar/calendar.helper";
import * as Control from "../Control/control.component";
import { IconButton } from "../IconButton";
import { Popover } from "../Popover";
import { DEFAULT_FORMATS, TRIGGER_ATTRIBUTE } from "./date-field.constant";
import {
  DateFieldProvider,
  InsideInputProvider,
  resolveFieldValue,
  useDateFieldContext,
  useInsideInputContext,
  type SharedContext,
} from "./date-field.context";
import type {
  CalendarViewOptions,
  CalendarViewProps,
  DateFieldRangeProps,
  DateFieldRootProps,
  DateFieldSingleProps,
  DateFieldUnit,
  RangePart,
} from "./date-field.type";
import {
  assignRangePeriod,
  isOutOfBounds,
  isRangeComplete,
  isRangeInverted,
  nextPartToFill,
  toPeriodPart,
} from "./helpers/date-range.helper";
import { useDateSegments } from "./hooks/use-date-segments.hook";
import { DayView } from "./views/day-view.component";
import { PeriodView } from "./views/period-view.component";

const SEGMENT_CLASS_NAME =
  "w-[2.5ch] cursor-text border-none bg-transparent p-0 text-center text-inherit outline-none focus:rounded-[4px] focus:bg-primary-blue focus:text-primary-white read-only:cursor-default disabled:cursor-default";

const WIDE_SEGMENT_CLASS_NAME = "w-[4ch]";

const assertDayjs = (value: unknown) => {
  if (value != null && !dayjs.isDayjs(value)) {
    throw new TypeError(
      `DateField: value must be a dayjs instance or null, received ${Object.prototype.toString.call(value)}. Wrap it with dayjs().`,
    );
  }
};

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/

const ROOT_NAME = "DateField.Root";

interface ProviderProps<TProps> {
  shared: SharedContext;
  field: TProps;
  children: React.ReactNode;
}

const SingleProvider = ({
  shared,
  field,
  children,
}: ProviderProps<Pick<DateFieldSingleProps, "value" | "defaultValue" | "onValueChange">>) => {
  const [value, onValueChange] = useControllableState({
    prop: field.value,
    defaultProp: field.defaultValue ?? null,
    onChange: field.onValueChange,
  });

  return (
    <DateFieldProvider {...shared} mode="single" value={value} onValueChange={onValueChange}>
      {children}
    </DateFieldProvider>
  );
};

const RangeProvider = ({
  shared,
  field,
  children,
}: ProviderProps<
  Pick<DateFieldRangeProps, "value" | "defaultValue" | "onValueChange" | "activePart" | "onActivePartChange">
>) => {
  const [value, onValueChange] = useControllableState({
    prop: field.value,
    defaultProp: field.defaultValue ?? EMPTY_DATE_RANGE,
    onChange: field.onValueChange,
  });
  const [activePart, setActivePart] = useControllableState<RangePart>({
    prop: field.activePart,
    defaultProp: "from",
    onChange: field.onActivePartChange,
  });

  return (
    <DateFieldProvider
      {...shared}
      mode="range"
      value={value}
      onValueChange={onValueChange}
      activePart={activePart}
      setActivePart={setActivePart}
    >
      {children}
    </DateFieldProvider>
  );
};

const Root = forwardRef<HTMLDivElement, DateFieldRootProps>((props, forwardedRef) => {
  const { unit = "day", min, max, disabled, readOnly, error, rounded, modal, className, children } = props;
  const [open, setOpen] = useControllableState({
    prop: props.open,
    defaultProp: props.defaultOpen ?? false,
    onChange: props.onOpenChange,
  });
  const anchorRef = useRef<HTMLDivElement>(null);
  const composedRef = useComposedRefs(forwardedRef, anchorRef);

  if (props.mode === "range") {
    const range = props.value ?? props.defaultValue;
    assertDayjs(range?.from);
    assertDayjs(range?.to);
  } else {
    assertDayjs(props.value ?? props.defaultValue);
  }

  const shared: SharedContext = {
    unit,
    format: props.format ?? DEFAULT_FORMATS[unit],
    min,
    max,
    disabled,
    readOnly,
    error,
    rounded,
    open,
    setOpen,
    anchorRef,
  };

  const frame = (
    <Popover.Anchor asChild>
      <div ref={composedRef} className={cn("gap-2.5 flex w-fit items-center", className)}>
        {children}
      </div>
    </Popover.Anchor>
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen} modal={modal}>
      {props.mode === "range" ? (
        <RangeProvider shared={shared} field={props}>
          {frame}
        </RangeProvider>
      ) : (
        <SingleProvider shared={shared} field={props}>
          {frame}
        </SingleProvider>
      )}
    </Popover.Root>
  );
});

Root.displayName = ROOT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Input
 * -----------------------------------------------------------------------------------------------*/

const INPUT_NAME = "DateField.Input";

interface InputProps extends Omit<React.ComponentPropsWithRef<"div">, "onPaste"> {
  part?: RangePart;
}

const Input = forwardRef<HTMLDivElement, InputProps>((props, forwardedRef) => {
  const { part = "from", id, className, children, ...divProps } = props;
  const context = useDateFieldContext(INPUT_NAME);
  const { value, onValueChange, active } = resolveFieldValue(context, part);
  const groupId = useId();

  const segments = useDateSegments({
    value,
    onValueChange,
    format: context.format,
    disabled: context.disabled,
    readOnly: context.readOnly,
  });

  const invalid =
    segments.isInvalid ||
    isOutOfBounds(value, context.min, context.max) ||
    (context.mode === "range" && isRangeInverted(context.value));

  return (
    <Control.Root
      {...divProps}
      ref={forwardedRef}
      error={context.error || invalid}
      rounded={context.rounded}
      data-part={context.mode === "range" ? part : undefined}
      data-active={context.mode === "range" ? active : undefined}
      data-invalid={invalid}
      data-empty={segments.isEmpty}
      className={cn(
        "items-center",
        context.open && active && !context.error && !invalid && "border-primary-blue",
        className,
      )}
      onPaste={segments.onPaste}
      // Only spinbutton re-aims range bound — focusin bubbles from slotted buttons.
      onFocusCapture={event => {
        if (context.mode !== "range") return;
        if ((event.target as HTMLElement).getAttribute("role") !== "spinbutton") return;
        context.setActivePart(part);
      }}
    >
      <div role="group" id={groupId} className="flex flex-1 items-center [font-variant-numeric:tabular-nums]">
        {segments.parts.map((segmentPart, index) =>
          segmentPart.kind === "literal" ? (
            <span key={index} aria-hidden className="whitespace-pre">
              {segmentPart.text}
            </span>
          ) : (
            <input
              key={index}
              {...segments.getSegmentProps(segmentPart.index)}
              id={segmentPart.index === 0 ? id : undefined}
              size={segmentPart.length}
              className={cn(SEGMENT_CLASS_NAME, segmentPart.length === 4 && WIDE_SEGMENT_CLASS_NAME)}
            />
          ),
        )}
      </div>
      <InsideInputProvider inside part={part} labelledBy={groupId}>
        {children}
      </InsideInputProvider>
    </Control.Root>
  );
});

Input.displayName = INPUT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

const TRIGGER_NAME = "DateField.Trigger";

interface TriggerProps extends React.ComponentPropsWithRef<"button"> {
  asChild?: boolean;
  part?: RangePart;
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>((props, forwardedRef) => {
  const { asChild, part: partProp, children, className, onClick, ...buttonProps } = props;
  const context = useDateFieldContext(TRIGGER_NAME);
  const { inside, part: insidePart, labelledBy } = useInsideInputContext(TRIGGER_NAME);
  const part = context.mode === "range" ? (partProp ?? insidePart ?? nextPartToFill(context.value)) : undefined;

  const button = (
    <Popover.Trigger asChild>
      <IconButton
        aria-labelledby={labelledBy}
        {...buttonProps}
        {...{ [TRIGGER_ATTRIBUTE]: "" }}
        data-part={part}
        ref={forwardedRef}
        asChild={asChild}
        disabled={context.disabled || context.readOnly}
        className={cn(inside && "h-auto w-auto bg-transparent active:bg-transparent", className)}
        onClick={composeEventHandlers(onClick, event => {
          if (context.mode !== "range" || !part) return;
          // Radix toggles the popover unless preventDefault when aiming the other range bound.
          if (context.open && part !== context.activePart) event.preventDefault();
          context.setActivePart(part);
        })}
      >
        {children ?? <CalendarIcon />}
      </IconButton>
    </Popover.Trigger>
  );

  return inside ? <Control.Slot side="right">{button}</Control.Slot> : button;
});

Trigger.displayName = TRIGGER_NAME;

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

const CONTENT_NAME = "DateField.Content";

type ContentProps = React.ComponentPropsWithRef<typeof Popover.Content>;

const Content = forwardRef<HTMLDivElement, ContentProps>(({ className, onInteractOutside, ...props }, forwardedRef) => {
  const context = useDateFieldContext(CONTENT_NAME);

  return (
    <Popover.Content
      {...props}
      ref={forwardedRef}
      className={cn("w-fit", className)}
      onInteractOutside={event => {
        onInteractOutside?.(event);
        if (event.defaultPrevented) return;
        const target = event.target as HTMLElement | null;
        if (target?.closest(`[${TRIGGER_ATTRIBUTE}]`) && context.anchorRef.current?.contains(target)) {
          event.preventDefault();
        }
      }}
    />
  );
});

Content.displayName = CONTENT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Calendar
 * -----------------------------------------------------------------------------------------------*/

const CALENDAR_NAME = "DateField.Calendar";

const ZOOM_OUT: Record<DateFieldUnit, DateFieldUnit | undefined> = { day: "month", month: "year", year: undefined };
const ZOOM_IN: Record<DateFieldUnit, DateFieldUnit> = { day: "day", month: "day", year: "month" };

interface CalendarProps {
  closeOnSelect?: boolean;
  views?: CalendarViewOptions;
}

const FieldCalendar = ({ closeOnSelect = false, views = {} }: CalendarProps) => {
  const context = useDateFieldContext(CALENDAR_NAME);
  const { unit, min, max } = context;
  const bounds = { min, max };
  const focused = context.mode === "single" ? context.value : (context.value[context.activePart] ?? context.value.from);
  const [view, setView] = useState(unit);
  const [displayed, setDisplayed] = useState(() => focused ?? max ?? dayjs());

  const select = (period: Dayjs) => {
    if (context.mode === "single") {
      context.onValueChange(toPeriodPart(period, unit, "from", bounds));
      if (closeOnSelect) context.setOpen(false);
      return;
    }
    const assignment = assignRangePeriod(context.value, context.activePart, period, unit, bounds);
    context.onValueChange(assignment.range);
    context.setActivePart(assignment.activePart);
    if (closeOnSelect && isRangeComplete(assignment.range)) context.setOpen(false);
  };

  const zoomOut = ZOOM_OUT[view];
  const viewProps: CalendarViewProps = {
    selection: context,
    displayed,
    onDisplayedChange: setDisplayed,
    onPeriodClick: period => {
      if (view === unit) return select(period);
      setDisplayed(period);
      setView(ZOOM_IN[view]);
    },
    onZoomOut: zoomOut && (() => setView(zoomOut)),
    ...bounds,
  };

  if (view === "day") return <DayView {...views.day} {...viewProps} />;
  return <PeriodView {...views[view]} {...viewProps} unit={view} />;
};

FieldCalendar.displayName = CALENDAR_NAME;

export { Slot } from "../Control/control.component";
export type { SlotProps } from "../Control/control.component";
export { Content, FieldCalendar as Calendar, Input, Root, Trigger };
export type { CalendarProps, ContentProps, InputProps, TriggerProps };
